import * as Header from '-/Custom/Header';
import { Stack, useLocalSearchParams } from 'expo-router';
import { ScrollView } from 'react-native';
import { useQuery } from 'urql';
import {
  EventRsvpEditorAccessFragment,
  getEventRsvpEditorAccess,
} from '@/components/Event/EventRsvpEditorAccess';
import EventRsvpList, {
  EventRsvpListFragment,
} from '@/components/Event/EventRsvpList';
import Box from '@/components/UI/Box';
import Text from '@/components/UI/Text';
import Icon from '@/components/Unistyles/Icon';
import { graphql, readFragment } from '@/gql/graphql';
import useUserId from '@/hooks/useUserId';

const GetEventRsvpDetail = graphql(/* GraphQL */ `
  query GetEventRsvpDetail(
    $eventId: uuid!
    $personId: String!
    $subjectPersonId: String!
  ) {
    event_by_pk(id: $eventId) {
      id
      title
      event @_optional {
        id
        title
      }
    }
    ...EventRsvpList @_optional
    ...EventRsvpEditorAccess @_optional
  }
`, [EventRsvpListFragment, EventRsvpEditorAccessFragment]);

export default function EventRsvpDetail() {
  const { eventId, subjectPersonId: requestedSubjectPersonId } =
    useLocalSearchParams<{
      eventId: string;
      subjectPersonId?: string;
    }>();
  const personId = useUserId();
  const subjectPersonId = requestedSubjectPersonId ?? personId;
  const [{ data }] = useQuery({
    query: GetEventRsvpDetail,
    variables: { eventId, personId, subjectPersonId },
  });
  const event = data?.event_by_pk;
  const rsvpData = readFragment(EventRsvpListFragment, data);
  const access = getEventRsvpEditorAccess({
    fragment: data,
    personId,
    subjectPersonId,
  });
  const selectedPerson =
    requestedSubjectPersonId && access.canEdit
      ? rsvpData?.selectedRsvpPerson
      : null;
  const selectedManagedPerson = rsvpData?.person_manager.find(
    (manager) => manager.person_id === selectedPerson?.id,
  );
  const selectedTeamTitle =
    access.team?.title ?? selectedManagedPerson?.team?.title;
  const headerTitle = () => (
    <Header.TitleContainer>
      <Box flex justifyContent="center" minWidth={0} overflow="hidden">
        <Text
          flexShrink={1}
          fontWeight="semibold"
          numberOfLines={1}
          size="lg"
          testID="ScreenHeaderPrimaryTitle"
        >
          {selectedPerson?.name ?? event?.title ?? 'RSVP'}
        </Text>
        <Box alignItems="center" direction="horizontal" space="sm">
          <Icon
            name={selectedPerson ? 'person-outline' : 'calendar-outline'}
            size="xs"
            variant="typographySecondary"
          />
          <Text
            flexShrink={1}
            numberOfLines={1}
            size="xs"
            variant="typographySecondary"
          >
            {selectedPerson
              ? (selectedTeamTitle ?? 'RSVP')
              : (event?.event?.title ?? 'RSVPs')}
          </Text>
        </Box>
      </Box>
    </Header.TitleContainer>
  );

  return (
    <Box flex>
      <Stack.Screen
        options={{
          headerTitle,
          title: selectedPerson?.name ?? event?.title ?? 'RSVP',
        }}
      />
      <ScrollView
        contentInsetAdjustmentBehavior="automatic"
        testID="EventRsvpListScrollView"
      >
        <Box p="md">
          <EventRsvpList
            fragment={data}
            personId={personId}
            subjectPersonId={requestedSubjectPersonId}
          />
        </Box>
      </ScrollView>
    </Box>
  );
}
